import { Box, ButtonGroup } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap';
import Filter from 'Components/Filter/Filter';
import SiteTable from 'FeatureModules/Site/Components/SiteTable/SiteTable';
import usePageTitle from 'hooks/usePageTitle';
import { CardIllustration, EmptyStateCard, InfoTooltip, ThemeButton } from 'DesignSystem';
import useSiteActions from 'FeatureModules/Site/hooks/useSiteActions';
import useSites from 'FeatureModules/Site/hooks/useSites';
import React, { FC } from 'react';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { ESiteViewOption } from 'FeatureModules/Site/types/site.types';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const Sites: FC = () => {
  usePageTitle('Sites');
  const {
    sites,
    summary,
    totalCount,
    loading,
    error,
    searchTerm,
    hasNextPage,
    viewOption,
    orderBy,
    filters,
    handleFilterChange,
    handleFilterReset,
    handleSearchTermChange,
    handlePageEndReached,
    handleSiteViewOptionChange,
    handleOrderByChange,
  } = useSites();
  const { handleCreateSite, handleCreateSiteCluster } = useSiteActions();

  if (error) return <ErrorState />;

  const FilterContainer = (
    <ButtonGroup size="small">
      <ThemeButton
        className={viewOption === ESiteViewOption.CLUSTER ? 'selected' : ''}
        onClick={() => handleSiteViewOptionChange(ESiteViewOption.CLUSTER)}
        typographyColor="ALMOST_BLACK"
      >
        Cluster view
      </ThemeButton>
      <ThemeButton
        className={viewOption === ESiteViewOption.LIST ? 'selected' : ''}
        onClick={() => handleSiteViewOptionChange(ESiteViewOption.LIST)}
        typographyColor="ALMOST_BLACK"
      >
        List view
      </ThemeButton>
    </ButtonGroup>
  );

  if (!sites.length && !searchTerm && !filters.some(({ value }) => !!value) && !loading) {
    return (
      <PageContainer>
        <PageTitle
          title="Sites"
          TitleAdornment={
            <Box ml={1}>
              <InfoTooltip
                size={EAvailableSizes.LARGE}
                text="Sites can be farms, processing plants, storage facilities, factories, etc. Manage the sites where supply chain activities are happening and who owns them."
              />
            </Box>
          }
        />
        <Box mb={3} />

        <FlexBox my={2} gap={1}>
          {FilterContainer}
        </FlexBox>

        <EmptyStateCard
          data-testid="sites-getting-started-card"
          data-testid-button="create-site-btn"
          title="Add sites to describe locations and to re-use them in different supply chain activities"
          variant={EItemTypes.LOCATION}
          buttonText="Add new site"
          background={EBackgroundTypes.WORLD}
          size={EAvailableSizes.LARGE}
          renderIllustration={
            <CardIllustration image={EIllustrationTypes.LOCATIONS} width={300} right={25} top={0} />
          }
          onCreateItemClick={() => handleCreateSite()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle
        title="Sites"
        TitleAdornment={
          <Box ml={1}>
            <InfoTooltip
              size={EAvailableSizes.LARGE}
              text="Sites can be farms, processing plants, storage facilities, factories, etc. Manage the sites where supply chain activities are happening and who owns them."
            />
          </Box>
        }
      />

      <Filter
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
      />

      <FlexBox my={2} gap={1}>
        {FilterContainer}
        <ThemeButton
          size="medium"
          color="WHITE"
          onClick={handleCreateSiteCluster}
          startIcon={<Plus size={20} color="black" />}
          data-testid="create-cluster-btn"
        >
          Add cluster
        </ThemeButton>
        <ThemeButton
          size="medium"
          color="PRIMARY"
          onClick={() => handleCreateSite()}
          startIcon={<Plus size={20} color="white" />}
          data-testid="create-site-btn"
        >
          Add site
        </ThemeButton>
      </FlexBox>

      <SiteTable
        maxHeight="calc(100vh - 332px)"
        sites={sites}
        loading={loading}
        totalCount={totalCount}
        summary={summary}
        orderBy={orderBy}
        hasMoreRows={hasNextPage}
        searchTerm={searchTerm}
        onEndReached={handlePageEndReached}
        onOrderByChange={handleOrderByChange}
        onSearchTermChange={handleSearchTermChange}
      />
    </PageContainer>
  );
};

export default Sites;
